/**
 * page10 batch-create (Q-T040) — `POST /worksheets/batch` mutation hook.
 *
 * A sibling `useBatchWorksheets` (#435 group-view) párja a CREATE oldalon. Plain-useState
 * (mint a batch-area többi hookja), így nincs QueryClient-provider függés a tesztekben.
 * Az `errorCode` az `ApiError.code`-ot adja vissza (400 dup-serial / >26 / 409 device-active
 * megkülönböztetéséhez a UI-ban).
 */
import { ApiError } from '@/api/client';
import {
  createWorksheetBatch,
  type CreateWorksheetBatchInput,
  type WorksheetBatchResponse,
} from '@/api/worksheets';
import { useCallback, useState } from 'react';

export function useCreateWorksheetBatch(): {
  create: (input: CreateWorksheetBatchInput) => Promise<WorksheetBatchResponse['data']>;
  isLoading: boolean;
  error: string | null;
  errorCode: string | null;
} {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const create = useCallback(async (input: CreateWorksheetBatchInput) => {
    setIsLoading(true);
    setError(null);
    setErrorCode(null);
    try {
      const response = await createWorksheetBatch(input);
      return response.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Batch-létrehozás sikertelen';
      setError(message);
      setErrorCode(err instanceof ApiError ? (err.code ?? null) : null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { create, isLoading, error, errorCode };
}
